import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useI18n } from '../i18n'
import {
  useWorkYard, useFarmInvoices, useCreateInvoice, useUpdateInvoice, useMembers, useMyFarm,
} from '../api/hooks'
import type { Invoice, InvoiceLine } from '@horsehub/shared'
import { formatPence, roleAtLeast } from '@horsehub/shared'
import { Modal, Input, Select, PageSpinner, EmptyState } from '../components/ui'
import { fmtDate } from '../lib/format'
import { toast } from '../lib/toast'
import { errorMessage } from '../lib/errors'

const STATUS_STYLE: Record<Invoice['status'], string> = {
  draft: '!bg-brand-100 !text-brand-700 dark:!bg-brand-800 dark:!text-brand-200',
  sent: '!bg-amber-100 !text-amber-700 dark:!bg-amber-900/40 dark:!text-amber-300',
  paid: '!bg-emerald-100 !text-emerald-700 dark:!bg-emerald-900/40 dark:!text-emerald-300',
  void: '!bg-brand-100 !text-brand-400 line-through dark:!bg-brand-800 dark:!text-brand-500',
}

/** The yard's livery invoicing — raise, send, mark paid. Managers and up. */
export function FarmInvoices() {
  const { t, locale } = useI18n()
  const { farmId, role, isLoading } = useWorkYard()
  const { data: invoices, isLoading: invLoading } = useFarmInvoices(farmId)
  const update = useUpdateInvoice(farmId ?? '')
  const [raising, setRaising] = useState(false)

  if (isLoading) return <PageSpinner />
  if (!farmId || !role || !roleAtLeast(role, 'manager')) {
    return (
      <main className="mx-auto max-w-3xl px-5 py-16 text-center">
        <p className="text-lg">🧾</p>
        <p className="mt-2 font-semibold">{t('errors.generic')}</p>
        <Link to="/farm" className="btn-primary mt-4 inline-block">{t('farm.billing.back')}</Link>
      </main>
    )
  }

  const act = (inv: Invoice, action: 'send' | 'markPaid' | 'void') =>
    update.mutate(
      { iid: inv.id, body: { action } },
      { onError: (e) => toast.error(errorMessage(e)) },
    )

  return (
    <main className="mx-auto max-w-4xl px-5 py-8 pb-24">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/farm" className="btn-ghost !px-3 !py-2 text-sm">←</Link>
          <h1 className="text-2xl font-extrabold tracking-tight">{t('farm.invoices.title')}</h1>
        </div>
        <button onClick={() => setRaising(true)} className="btn-primary !px-4 !py-2.5 text-sm">{t('farm.invoices.raise')}</button>
      </div>

      {invLoading ? (
        <PageSpinner />
      ) : !invoices || invoices.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            icon="🧾"
            title={t('farm.invoices.empty')}
            body={t('farm.invoices.emptyBody')}
            action={<button onClick={() => setRaising(true)} className="btn-primary">{t('farm.invoices.raise')}</button>}
          />
        </div>
      ) : (
        <ul className="mt-6 space-y-3">
          {invoices.map((inv) => (
            <li key={inv.id} className="card p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{inv.number}</span>
                    <span className={`chip !px-2 !py-0.5 text-xs ${STATUS_STYLE[inv.status]}`}>{t(`farm.invoices.st.${inv.status}`)}</span>
                  </div>
                  <div className="truncate text-sm text-brand-600 dark:text-brand-300">
                    {inv.memberName}
                    {inv.periodLabel ? ` · ${inv.periodLabel}` : ''}
                    {inv.sentAt && inv.status === 'sent' ? ` · ${t('farm.invoices.sentOn', { date: fmtDate(inv.sentAt, locale) })}` : ''}
                    {inv.paidAt && inv.status === 'paid' ? ` · ${t('farm.invoices.paidOn', { date: fmtDate(inv.paidAt, locale) })}` : ''}
                  </div>
                </div>
                <div className="text-xl font-extrabold">{formatPence(inv.totalPence)}</div>
              </div>

              <ul className="mt-2 divide-y divide-brand-100/70 dark:divide-brand-700/40 text-sm">
                {inv.lines.map((l, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 py-1.5">
                    <span className="min-w-0 truncate">{l.description}</span>
                    <span className="shrink-0 font-semibold">{formatPence(l.amountPence)}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-3 flex flex-wrap gap-2">
                {inv.status === 'draft' && (
                  <>
                    <button onClick={() => act(inv, 'send')} disabled={update.isPending} className="btn-primary !px-4 !py-2 text-sm disabled:opacity-60">
                      {t('farm.invoices.send', { name: inv.memberName })}
                    </button>
                    <button onClick={() => act(inv, 'void')} disabled={update.isPending} className="btn-ghost !px-4 !py-2 text-sm disabled:opacity-60">
                      {t('farm.invoices.voidIt')}
                    </button>
                  </>
                )}
                {inv.status === 'sent' && (
                  <>
                    <button onClick={() => act(inv, 'markPaid')} disabled={update.isPending} className="btn-primary !px-4 !py-2 text-sm disabled:opacity-60">
                      {t('farm.invoices.markPaid')}
                    </button>
                    <button onClick={() => act(inv, 'void')} disabled={update.isPending} className="btn-ghost !px-4 !py-2 text-sm disabled:opacity-60">
                      {t('farm.invoices.voidIt')}
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {raising && <RaiseInvoiceModal farmId={farmId} onClose={() => setRaising(false)} />}
    </main>
  )
}

function RaiseInvoiceModal({ farmId, onClose }: { farmId: string; onClose: () => void }) {
  const { t } = useI18n()
  const create = useCreateInvoice(farmId)
  const { data: members } = useMembers(farmId)
  const { data: farm } = useMyFarm()
  const [memberId, setMemberId] = useState('')
  const [auto, setAuto] = useState(true)
  const [lines, setLines] = useState<Array<{ description: string; pounds: string }>>([{ description: '', pounds: '' }])
  const [periodLabel, setPeriodLabel] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [err, setErr] = useState('')

  const liveries = (members ?? []).filter((m) => (m.horseCount ?? 0) > 0 || m.role === 'boarder')
  const hasPrices = Object.values(farm?.packagePrices ?? {}).some((v) => typeof v === 'number' && v > 0)

  const manualLines = (): InvoiceLine[] =>
    lines
      .filter((l) => l.description.trim() && l.pounds !== '' && Number.isFinite(Number(l.pounds)))
      .map((l) => ({ description: l.description.trim(), amountPence: Math.round(Number(l.pounds) * 100) }))

  const save = async () => {
    if (!memberId) {
      setErr(t('errors.required', { fields: t('farm.invoices.billTo') }))
      return
    }
    const manual = manualLines()
    if (!auto && manual.length === 0) {
      setErr(t('errors.required', { fields: t('farm.invoices.line') }))
      return
    }
    setErr('')
    try {
      await create.mutateAsync({
        memberId,
        autoFromPackages: auto && manual.length === 0,
        lines: manual.length > 0 ? manual : undefined,
        periodLabel: periodLabel.trim() || undefined,
        dueDate: dueDate || undefined,
      })
      onClose()
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }

  return (
    <Modal title={t('farm.invoices.raiseTitle')} onClose={onClose}>
      <div className="space-y-4">
        <Select
          label={t('farm.invoices.billTo')}
          value={memberId}
          onChange={setMemberId}
          options={[
            { value: '', label: '—' },
            ...liveries.map((m) => ({ value: m.userId, label: `${m.name} (${m.horseCount ?? 0} 🐎)` })),
          ]}
        />

        {/* Build from packages — the two-tap path */}
        <label className="flex items-start gap-3 rounded-xl border border-brand-200 dark:border-brand-700 p-3">
          <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} className="mt-1 h-5 w-5" />
          <span>
            <span className="block font-semibold">{t('farm.invoices.autoFill')}</span>
            <span className="block text-sm text-brand-600 dark:text-brand-300">
              {hasPrices ? t('farm.invoices.autoFillHint') : t('farm.invoices.noPrices')}
            </span>
          </span>
        </label>

        {!auto && (
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="flex gap-2">
                <input
                  value={l.description}
                  placeholder={t('farm.invoices.lineDesc')}
                  onChange={(e) => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))}
                  className="min-w-0 flex-1 rounded-xl border border-brand-200 dark:border-brand-700 bg-white dark:bg-brand-900 px-3 py-2 outline-none focus:border-brand-500"
                />
                <div className="flex w-28 shrink-0 items-center gap-1">
                  <span className="text-brand-600 dark:text-brand-300">£</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={l.pounds}
                    onChange={(e) => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, pounds: e.target.value } : x)))}
                    className="w-full rounded-xl border border-brand-200 dark:border-brand-700 bg-white dark:bg-brand-900 px-3 py-2 outline-none focus:border-brand-500"
                  />
                </div>
              </div>
            ))}
            <button onClick={() => setLines((ls) => [...ls, { description: '', pounds: '' }])} className="text-sm font-semibold text-brand-700 dark:text-brand-200 hover:underline">
              {t('farm.invoices.addLine')}
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Input label={t('farm.invoices.period')} value={periodLabel} onChange={setPeriodLabel} />
          <Input label={t('farm.invoices.dueDate')} value={dueDate} onChange={setDueDate} type="date" />
        </div>

        {err && <p className="text-sm font-semibold text-red-600 dark:text-red-400">{err}</p>}
        <button onClick={save} disabled={create.isPending} className="btn-primary w-full disabled:opacity-60">
          {create.isPending ? t('farm.invoices.creating') : t('farm.invoices.create')}
        </button>
      </div>
    </Modal>
  )
}
